import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiBaseUrl } from '../config/env';
import { storage } from '../utils/storage';
import { useAuth } from './AuthContext';

interface NotificationContextValue {
  unreadCount: number;
  clearBadge: () => void;
  refreshUnreadCount: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue>({
  unreadCount: 0,
  clearBadge: () => {},
  refreshUnreadCount: async () => {},
});

export const useNotification = () => useContext(NotificationContext);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const { user } = useAuth();

  const refreshUnreadCount = async () => {
    try {
      const token = await storage.getToken();
      if (!token) return;

      const response = await fetch(`${apiBaseUrl}/api/notifications/unread-count`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setUnreadCount(data.count);
      }
    } catch (error) {
      console.error('Error fetching unread notification count:', error);
    }
  };

  const clearBadge = () => {
    setUnreadCount(0);
  };

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    refreshUnreadCount();

    const interval = setInterval(refreshUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  return (
    <NotificationContext.Provider value={{ unreadCount, clearBadge, refreshUnreadCount }}>
      {children}
    </NotificationContext.Provider>
  );
};
